import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const services = [
  {
    icon: 'ri-search-2-line',
    title: 'Product Sourcing',
    subtitle: 'Wij vinden jouw product bij de beste fabriek',
    description: 'Geen idee waar je moet beginnen? DUC heeft een uitgebreid netwerk van gekwalificeerde fabrikanten in China. Wij zoeken de juiste producent voor jouw specifieke product — op basis van kwaliteit, prijs en betrouwbaarheid.',
    features: [
      'Meer dan 2.000 gekwalificeerde fabrieken in ons netwerk',
      'Categoriespecialisten per producttype',
      'Sample coördinatie en beoordeling',
      'Prijsvergelijking tussen meerdere fabrieken',
      'MOQ-onderhandeling op maat',
      'Directe communicatie met fabrieken',
    ],
    image: 'https://public.readdy.ai/ai/img_res/bac3772a841b039ba0c14404edae95df.jpg',
    color: 'bg-[#0A1628]',
    dark: true,
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Kwaliteitscontrole',
    subtitle: 'Geen verrassingen bij aankomst',
    description: 'Onze kwaliteitscontrole zorgt ervoor dat jouw producten voldoen aan de afgesproken eisen. We voeren steekproefgewijs inspecties uit voor en na productie, zodat je weet wat je krijgt.',
    features: [
      'Fabrieksinspecties voor productiestart',
      'Pre-productie monsters beoordelen',
      'Steekproefsgewijze controles tijdens productie',
      'Eindinspectie voor verzending',
      'Foto- en videodocumentatie',
      'Heldere rapportage in het Nederlands',
    ],
    image: 'https://readdy.ai/api/search-image?query=quality%20control%20inspector%20checking%20products%20in%20warehouse%20factory%20checking%20boxes%20clipboard%20professional%20inspection%20industrial%20clean%20bright%20modern%20facility&width=700&height=480&seq=svc-qc-001&orientation=landscape',
    color: 'bg-[#F7F8FA]',
    dark: false,
  },
  {
    icon: 'ri-ship-line',
    title: 'Logistiek & DDP Levering',
    subtitle: 'All-in geleverd aan je deur',
    description: 'Wij regelen de volledige verzending van China naar jouw deur in Nederland. DDP (Delivered Duty Paid) betekent dat alle kosten — transport, invoerrechten en BTW — al zijn inbegrepen in de prijs. Geen verrassingen achteraf.',
    features: [
      'Zee-, lucht- en railvervoer mogelijk',
      'DDP levering — invoerrechten & BTW inbegrepen',
      'Realtime track & trace',
      'Volledige douaneafhandeling',
      'Bezorging aan jouw deur in Nederland',
      'Opslag bij vertraging mogelijk',
    ],
    image: 'https://readdy.ai/api/search-image?query=container%20ship%20cargo%20port%20logistics%20international%20shipping%20freight%20transportation%20ocean%20sea%20professional%20industrial%20aerial%20view%20modern%20harbor%20facility&width=700&height=480&seq=svc-logistics-001&orientation=landscape',
    color: 'bg-white',
    dark: false,
  },
  {
    icon: 'ri-paint-brush-line',
    title: 'Private Label & Maatwerk',
    subtitle: 'Jouw product, jouw merk',
    description: 'Wil je producten met jouw eigen branding? DUC regelt volledig maatwerk — van jouw logo op de verpakking tot specifieke kleuren, maten en materialen. Zo bouw je een echte merkidentiteit.',
    features: [
      'Eigen logo en branding op product en verpakking',
      'Maatwerk in kleuren, maten en materialen',
      'OEM & ODM productie',
      'Verpakkingsontwerp en -productie',
      'Kleine MOQ voor private label mogelijk',
      'Volledig begeleide productie',
    ],
    image: 'https://readdy.ai/api/search-image?query=private%20label%20branding%20product%20packaging%20custom%20logo%20design%20white%20label%20boxes%20modern%20clean%20minimal%20professional%20business%20brand%20identity%20packaging&width=700&height=480&seq=svc-label-001&orientation=landscape',
    color: 'bg-[#F7F8FA]',
    dark: false,
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Leveranciersbeheer',
    subtitle: 'Langdurige relaties, consistente kwaliteit',
    description: 'Eenmalig inkopen is één ding. Maar als je structureel wil groeien, heb je betrouwbare leveranciers nodig. DUC bouwt en onderhoudt leveranciersrelaties voor jou — zodat jij altijd een vaste, kwalitatieve bron hebt.',
    features: [
      'Leveranciersscoring en monitoring',
      'Contractbeheer en heronderhandeling',
      'Prestatierapportages per leverancier',
      'Back-up leveranciernetwerk',
      'Periodieke fabrieksevaluaties',
      'Prioritaire behandeling als vaste klant',
    ],
    image: 'https://readdy.ai/api/search-image?query=business%20supplier%20management%20meeting%20handshake%20partnership%20cooperation%20professional%20office%20modern%20industrial%20background%20supply%20chain%20management%20relationship%20building&width=700&height=480&seq=svc-supplier-001&orientation=landscape',
    color: 'bg-white',
    dark: false,
  },
  {
    icon: 'ri-money-euro-circle-line',
    title: 'Prijsoptimalisatie',
    subtitle: 'Altijd de scherpste inkoopprijs',
    description: 'Dankzij ons netwerk en onze onderhandelingservaring halen wij voor jou de laagst mogelijke prijs — zonder concessies op kwaliteit. Wij benchmarken meerdere fabrieken en kiezen de beste optie.',
    features: [
      'Prijsvergelijking tussen meerdere leveranciers',
      'Professionele onderhandelingen in het Chinees',
      'Inzicht in eerlijke markttarieven',
      'Advies over optimale bestelhoeveelheden',
      'Kostenbesparing van 30-80% t.o.v. Nederlandse inkoopprijzen',
      'Transparante offertes zonder verborgen kosten',
    ],
    image: 'https://readdy.ai/api/search-image?query=business%20pricing%20analysis%20chart%20data%20dashboard%20finance%20savings%20money%20cost%20reduction%20optimization%20professional%20modern%20clean%20office%20calculator%20documents&width=700&height=480&seq=svc-price-001&orientation=landscape',
    color: 'bg-[#0A1628]',
    dark: true,
  },
];

function ServiceCard({ service, index }: { service: typeof services[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.1 + (index % 2) * 0.1 }}
      className={`rounded-2xl overflow-hidden ${service.dark ? service.color : `${service.color} border border-[#0A1628]/6`}`}
    >
      <div className="grid lg:grid-cols-2 gap-0">
        <div className={`order-${index % 2 === 0 ? '2' : '1'} relative h-64 lg:h-auto overflow-hidden`}>
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover object-top"
          />
          {service.dark && (
            <div className="absolute inset-0 bg-[#0A1628]/40" />
          )}
        </div>
        <div className={`order-${index % 2 === 0 ? '1' : '2'} p-8 lg:p-10 flex flex-col justify-center`}>
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 ${service.dark ? 'bg-[#3B9EE8]/20' : 'bg-[#3B9EE8]/12'}`}>
            <i className={`${service.icon} text-[#3B9EE8] text-2xl`} />
          </div>
          <p className={`text-sm font-600 mb-2 ${service.dark ? 'text-[#3B9EE8]' : 'text-[#3B9EE8]'}`}>{service.subtitle}</p>
          <h3 className={`font-heading font-800 text-2xl lg:text-3xl mb-4 ${service.dark ? 'text-white' : 'text-[#0A1628]'}`}>{service.title}</h3>
          <p className={`text-sm leading-relaxed mb-6 ${service.dark ? 'text-white/60' : 'text-[#0A1628]/60'}`}>{service.description}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
            {service.features.map((feat) => (
              <li key={feat} className="flex items-start gap-2">
                <i className="ri-check-line text-[#3B9EE8] text-sm mt-0.5 flex-shrink-0" />
                <span className={`text-xs font-500 ${service.dark ? 'text-white/70' : 'text-[#0A1628]/65'}`}>{feat}</span>
              </li>
            ))}
          </ul>
          <a
            href="/#contact"
            className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-6 py-3 rounded-lg text-sm font-700 transition-all duration-200 cursor-pointer whitespace-nowrap self-start"
          >
            Vraag offerte aan
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function DienstenPage() {
  useSEO({
    title: 'Diensten Inkopen uit China | Product Sourcing, Private Label & DDP Import | DUC',
    description: 'Onze diensten voor inkopen uit China: product sourcing, private label productie, kwaliteitscontrole, DDP levering en leveranciersbeheer. Bespaar 30–80%. Gratis offerte.',
    keywords: 'product sourcing China diensten, private label China service, DDP levering import, kwaliteitscontrole China, leverancier vinden China, inkoop bemiddeling',
    canonical: `${SITE_URL}/diensten`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Diensten', item: `${SITE_URL}/diensten` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'DUC Diensten — Inkopen uit China',
        description: 'Alle diensten van Direct Uit China voor het goedkoop inkopen van producten bij fabrikanten in China.',
        url: `${SITE_URL}/diensten`,
        numberOfItems: 6,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Product Sourcing',
            url: `${SITE_URL}/diensten/product-sourcing`,
            item: {
              '@type': 'Service',
              name: 'Product Sourcing China',
              description: 'Wij vinden uw product bij de beste fabriek in China via ons netwerk van 2.000+ geverifieerde fabrikanten.',
              provider: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Kwaliteitscontrole',
            url: `${SITE_URL}/diensten/prijsoptimalisatie`,
            item: {
              '@type': 'Service',
              name: 'Kwaliteitscontrole China',
              description: 'Steekproefsgewijze inspecties voor en na productie. Geen verrassingen bij aankomst.',
              provider: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'DDP Levering',
            url: `${SITE_URL}/diensten/ddp-levering`,
            item: {
              '@type': 'Service',
              name: 'Logistiek & DDP Levering uit China',
              description: 'All-in geleverd aan uw deur. Transport, invoerrechten en BTW inbegrepen.',
              provider: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
            },
          },
          {
            '@type': 'ListItem',
            position: 4,
            name: 'Private Label',
            url: `${SITE_URL}/diensten/private-label`,
            item: {
              '@type': 'Service',
              name: 'Private Label & Maatwerk China',
              description: 'Producten met uw eigen logo en branding produceren in China. OEM & ODM productie.',
              provider: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
            },
          },
          {
            '@type': 'ListItem',
            position: 5,
            name: 'Leveranciersbeheer',
            url: `${SITE_URL}/diensten/leveranciersbeheer`,
            item: {
              '@type': 'Service',
              name: 'Leveranciersbeheer China',
              description: 'Langdurige relaties met vaste leveranciers in China voor consistente kwaliteit.',
              provider: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
            },
          },
          {
            '@type': 'ListItem',
            position: 6,
            name: 'Prijsoptimalisatie',
            url: `${SITE_URL}/diensten/prijsoptimalisatie`,
            item: {
              '@type': 'Service',
              name: 'Prijsoptimalisatie China',
              description: 'Altijd de scherpste inkoopprijs door benchmarking en professionele onderhandeling.',
              provider: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
            },
          },
        ],
      },
    ],
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-14 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20industrial%20factory%20production%20line%20machinery%20professional%20manufacturing%20facility%20dark%20blue%20tones%20minimal%20clean%20sophisticated%20dramatic%20lighting&width=1920&height=600&seq=diensten-hero-001&orientation=landscape"
            alt="DUC Diensten"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/80 to-[#0A1628]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line" />
              Terug naar home
            </Link>
          </motion.div>
          <motion.span initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-briefcase-4-line" />
            Onze diensten
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }} className="font-heading font-800 text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
            Alles wat je nodig hebt<br />
            <span className="text-[#3B9EE8]">voor slimmer inkopen</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }} className="text-white/60 text-lg max-w-2xl leading-relaxed">
            Van product sourcing tot DDP levering — DUC regelt het hele traject. Bespaar 30-80% op je inkoop zonder gedoe.
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col gap-8">
            {services.map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <i className="ri-customer-service-2-line text-[#3B9EE8] text-4xl mb-6 block" />
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
            Klaar om te starten?
          </h2>
          <p className="text-white/55 text-lg mb-8 leading-relaxed">
            Vertel ons wat je zoekt en ontvang binnen 24 uur een gratis en vrijblijvende prijsindicatie.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#contact" className="inline-flex items-center justify-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-mail-send-line" />
              Gratis offerte aanvragen
            </a>
            <a href="https://wa.me/31624359162" target="_blank" rel="nofollow noreferrer" className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-[#3B9EE8]/60 hover:text-[#3B9EE8] px-8 py-4 rounded-lg font-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-xl" />
              WhatsApp ons
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
